/**
 * Main application knowledge — Chat page
 */
import "./style.css";
import lod_ from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Icon, IconButton, Tooltip } from "@mui/material";
import { v4 } from "uuid";
import { t } from "i18next";

import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import DashboardLayout from "components/Advanced/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Advanced/Navbars/DashboardNavbar";
import { parseFilters, getLocalStorageBackValues } from "components/Custom/Filters/filters";

import FaiqActions from "redux-react/actions/faiqActions";
import Message from "./components/Message";
import appendAssistantMessageItem from "./messageEvents";

const OUTPUT_MODES = [
	{ value: "llm", labelKey: "CONFIG.answer" },
	{ value: "chunk", labelKey: "CONFIG.chunk" },
	{ value: "document", labelKey: "CONFIG.document" }
];

const DEFAULT_OUTPUT_STEP = "chunk";
const OUTPUT_STEP_STORAGE_PREFIX = "faiq-output-step";

/**
 * Read the persisted output step for a given assistant (falls back to default).
 * @param {string} assistantID
 */
const getStoredOutputStep = assistantID => {
	if (!assistantID) return DEFAULT_OUTPUT_STEP;
	try {
		return (
			localStorage.getItem(`${OUTPUT_STEP_STORAGE_PREFIX}-${assistantID}`) || DEFAULT_OUTPUT_STEP
		);
	} catch (e) {
		return DEFAULT_OUTPUT_STEP;
	}
};

/**
 * Persist the output step for a given assistant.
 * @param {string} assistantID
 * @param {string} value
 */
const setStoredOutputStep = (assistantID, value) => {
	if (!assistantID) return;
	try {
		localStorage.setItem(`${OUTPUT_STEP_STORAGE_PREFIX}-${assistantID}`, value);
	} catch (e) {
		// ignore storage errors (e.g. private mode / quota)
	}
};

/**
 * Radio-style mode selector rendered below the input.
 */
const ModeSelector = ({ value, onChange }) => (
	<div className="faiq-mode-selector" role="radiogroup">
		{OUTPUT_MODES.map(mode => (
			<button
				disabled={mode.value === "document"}
				key={mode.value}
				type="button"
				role="radio"
				aria-checked={value === mode.value}
				className={`faiq-mode-btn ${value === mode.value ? "active" : ""}`}
				onClick={() => onChange(mode.value)}
			>
				{t(mode.labelKey)}
			</button>
		))}
	</div>
);

/**
 * Empty state shown when the chat is fresh.
 */
const EmptyState = () => (
	<Box className="faiq-empty-state">
		<Box className="faiq-empty-state-icon">
			<Icon className="material-icons">forum</Icon>
		</Box>
		<MDTypography className="faiq-empty-state-title">{t("FAIQ.CHAT.welcome")}</MDTypography>
		<MDTypography className="faiq-empty-state-subtitle">
			{t("FAIQ.CHAT.welcomeSubtitle")}
		</MDTypography>
	</Box>
);

/**
 * Auto-growing textarea for the input.
 */
const ChatInput = ({
	value,
	onChange,
	onSend,
	onReset,
	disabled,
	parametersOutputStep,
	setParametersOutputStep
}) => {
	const textRef = useRef(null);

	useEffect(() => {
		if (!textRef.current) return;
		textRef.current.style.height = "auto";
		textRef.current.style.height = `${Math.min(textRef.current.scrollHeight, 160)}px`;
	}, [value]);

	const handleKeyDown = e => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			onSend();
		}
	};

	const canSend = value.trim().length > 0;

	return (
		<>
			<div className="faiq-input-box">
				<textarea
					ref={textRef}
					className="faiq-input-textarea"
					placeholder={t("FAIQ.CHAT.inputPlaceholder")}
					value={value}
					onChange={e => onChange(e.target.value)}
					onKeyDown={handleKeyDown}
					rows={1}
					disabled={disabled}
				/>
				<div className="faiq-input-actions">
					<Tooltip title={t("FAIQ.CHAT.newChat")} placement="top">
						<IconButton size="small" onClick={onReset} sx={{ color: "#6b7280" }}>
							<Icon fontSize="small">replay</Icon>
						</IconButton>
					</Tooltip>
					<Tooltip title={t("FAIQ.CHAT.send")} placement="top">
						<span>
							<IconButton
								size="small"
								onClick={onSend}
								disabled={!canSend}
								className={`faiq-send-btn ${!canSend ? "disabled" : ""}`}
							>
								<Icon fontSize="small">arrow_upward</Icon>
							</IconButton>
						</span>
					</Tooltip>
				</div>
			</div>
			<ModeSelector value={parametersOutputStep} onChange={setParametersOutputStep} />
			<div className="faiq-input-hint">{t("FAIQ.CHAT.inputHint")}</div>
		</>
	);
};

/**
 * Main component
 */
export default function ChatPage({ route }) {
	const endRef = useRef(null);
	const { profile, filters } = useSelector(state => state);
	const dispatch = useDispatch();

	const [pageFilters, setPageFilters] = useState([]);
	const [messages, setMessages] = useState([]);
	const [inputValue, setInputValue] = useState("");
	const [threadID, setThreadID] = useState(null);
	const [temporalMessage, setTemporalMessage] = useState("");
	const { assistantID } = profile.selectedAssistant;
	const [parametersOutputStep, setParametersOutputStep] = useState(() =>
		getStoredOutputStep(assistantID)
	);

	// Restore the persisted output step when the selected assistant changes
	useEffect(() => {
		setParametersOutputStep(getStoredOutputStep(assistantID));
	}, [assistantID]);

	// Update state + persist for the current assistant
	const handleOutputStepChange = value => {
		setParametersOutputStep(value);
		setStoredOutputStep(assistantID, value);
	};

	const scrollToBottom = () => {
		if (endRef.current) {
			endRef.current.scrollIntoView({ behavior: "smooth" });
		}
	};

	/**
	 * Update (or insert) the assistant message identified by questionID.
	 * @param {string} questionID
	 * @param {object} patch - fields to merge into the assistant message
	 */
	const upsertAssistantMessage = (questionID, patchOrUpdater) => {
		setMessages(prev => {
			const others = prev.filter(item => item.id !== questionID);
			const current = prev.find(item => item.id === questionID) || {
				id: questionID,
				direction: "in",
				sources: []
			};
			const patch = typeof patchOrUpdater === "function" ? patchOrUpdater(current) : patchOrUpdater;
			return [...others, { ...current, ...patch }];
		});
	};

	const sendMessage = async () => {
		if (!inputValue.trim()) return;

		const buildedFilters = getLocalStorageBackValues(
			profile.selectedAssistant.assistantID,
			route.route,
			filters
		);

		const metadatas = {};
		Object.entries(buildedFilters).forEach(([key, value]) => {
			metadatas[key] = value.value;
		});

		const question = inputValue;
		setTemporalMessage("");
		setInputValue("");

		const questionID = v4();
		const currentThreadID = lod_.cloneDeep(threadID);

		// Push the user message + an empty, loading assistant placeholder
		setMessages(prev => [
			...prev,
			{ message: question, direction: "out", id: v4() },
			{
				id: questionID,
				direction: "in",
				message: "",
				messageItems: [],
				sources: [],
				loading: true,
				phase: "analysis",
				threadID: currentThreadID
			}
		]);

		dispatch(
			FaiqActions.tryFAIQ(
				profile.assistantID,
				question,
				questionID,
				threadID,
				metadatas,
				{ parameters: { outputStep: parametersOutputStep } },
				{
					before: () => {},
					chunk: ({ type = "chunk", content = null, token = null }) => {
						switch (type) {
							// Backend signals which processing step it is on
							case "phase": {
								upsertAssistantMessage(questionID, { loading: true, phase: content });
								break;
							}
							// Knowledge message (outputStep "chunk" — no LLM)
							case "message": {
								upsertAssistantMessage(questionID, current => {
									const messageItems = appendAssistantMessageItem(current.messageItems, content);
									const lastItem = messageItems[messageItems.length - 1];

									return {
										loading: false,
										phase: null,
										messageItems,
										message: lastItem?.message ?? current.message ?? "",
										sources: lastItem?.sources ?? []
									};
								});
								break;
							}
							case "error": {
								upsertAssistantMessage(questionID, {
									loading: false,
									phase: null,
									error: true,
									message: t("FAIQ.CHAT.serviceUnavailable"),
									messageItems: [],
									sources: []
								});
								break;
							}
							case "end": {
								const sources = content?.sources ?? [];
								const reply = content?.reply;
								const endType = content?.type;
								// In "chunk" mode the text was already streamed; only attach sources
								if (endType === "chunk") {
									upsertAssistantMessage(questionID, { loading: false, phase: null, sources });
									break;
								}
								setTemporalMessage(prevTemporal => {
									upsertAssistantMessage(questionID, {
										loading: false,
										phase: null,
										message: reply || prevTemporal,
										messageItems: [],
										sources
									});
									return prevTemporal;
								});
								break;
							}
							// Streamed answer token
							case "chunk": {
								setTemporalMessage(prevMessage => {
									const update = prevMessage + (content ?? "");
									upsertAssistantMessage(questionID, {
										loading: false,
										phase: null,
										messageItems: [],
										message: update
									});
									return update;
								});
								break;
							}
							default:
								break;
						}
					},
					after: () => {
						setTemporalMessage("");
					},
					error: () => {
						upsertAssistantMessage(questionID, {
							loading: false,
							phase: null,
							error: true,
							message: t("FAIQ.CHAT.serviceUnavailable"),
							messageItems: [],
							sources: []
						});
					}
				}
			)
		);
	};

	const resetChat = () => {
		setMessages([]);
		setThreadID(v4());
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	useEffect(() => {
		resetChat();
	}, [profile.assistantID]);

	async function getPageFilters() {
		return new Promise(resolve => {
			dispatch(
				FaiqActions.getMetadatasFilters(res => {
					resolve(res);
				})
			);
		});
	}

	useEffect(() => {
		async function load() {
			const fetched = await getPageFilters();
			setPageFilters(fetched);
		}
		load();
	}, [profile.selectedAssistant.assistantID, route]);

	const filtersJsx = parseFilters(profile.assistantID, route.route, pageFilters, filters, dispatch);

	return (
		<DashboardLayout flex>
			<DashboardNavbar
				filters={[
					<div className="faiq-header-bar" key="faiq-header">
						<div className="faiq-header-actions">
							<Tooltip title={t("FAIQ.CHAT.newChat")} placement="bottom">
								<IconButton size="small" onClick={resetChat}>
									<Icon fontSize="small">add</Icon>
								</IconButton>
							</Tooltip>
						</div>
						{filtersJsx && (
							<MDBox display="flex" flexWrap="wrap" gap={1}>
								{filtersJsx}
							</MDBox>
						)}
					</div>
				]}
			/>

			<MDBox className="faiq-chat-page">
				<MDBox className="faiq-chat-scroll">
					<MDBox className="faiq-chat-container">
						{messages.length === 0 ? (
							<EmptyState />
						) : (
							messages.map((message, index) => (
								<Message
									key={message.id || `m-${index}`}
									id={message.id}
									threadID={message.threadID}
									direction={message.direction}
									sources={(message.sources ?? []).filter(
										source => source?.metadatas?.url || source?.url
									)}
									message={message.message}
									messageItems={message.messageItems}
									loading={message.loading}
									phase={message.phase}
									error={message.error}
									assistantID={profile.assistantID}
								/>
							))
						)}
						<div ref={endRef}></div>
					</MDBox>
				</MDBox>

				<Box className="faiq-input-area">
					<Box className="faiq-input-container">
						<ChatInput
							value={inputValue}
							onChange={setInputValue}
							onSend={sendMessage}
							onReset={resetChat}
							parametersOutputStep={parametersOutputStep}
							setParametersOutputStep={handleOutputStepChange}
						/>
					</Box>
				</Box>
			</MDBox>
		</DashboardLayout>
	);
}
